import { IntlShape } from 'react-intl';
import { Unsubscribe } from 'router5/dist/types/base';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { IEventTrackerDetailsDTO } from '../../api/events.dao';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { EventForm } from '../_events/_events-list/_event-form/event-form.container';
import PlanningNotesFormVM from './_planning-notes-form/planning-notes-form.viewmodel';
import { PlanningNotesForm } from './_planning-notes-form/planning-notes.container';
import { EventTrackerVM } from './event-tracker.viewmodel';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../tracking-service/tracking-values';

class ManageEventTrackerViewModal implements IModuleVM {
    @mobx.observable
    public composedIdToSubWindowMap: { [index: string]: ISubWindowFacade };

    @mobx.observable
    public page: Routes;
    @mobx.observable
    public upcomingEventTrackerVM: EventTrackerVM;
    @mobx.observable
    public pastEventTrackerVM: EventTrackerVM;
    @mobx.observable
    public deliveredEventTrackerVM: EventTrackerVM;

    private subscriptions: Unsubscribe[];

    constructor(
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.composedIdToSubWindowMap = {};
        this.subscriptions = [];
    }

    @mobx.action
    didMount(): void {
        this.upcomingEventTrackerVM = this.initEventTrackerVM(Routes.EventTrackerUpcoming);
        this.pastEventTrackerVM = this.initEventTrackerVM(Routes.EventTrackerPast);
        this.deliveredEventTrackerVM = this.initEventTrackerVM(Routes.EventTrackerDelivered);
        this.initRouteListeners();
    }

    willUnmount(): void {
        this.upcomingEventTrackerVM.willUnmount();
        this.pastEventTrackerVM.willUnmount();
        this.deliveredEventTrackerVM.willUnmount();
        this.subscriptions.forEach(x => x());
    }

    private initRouteListeners = () => {
        this.subscriptions.push(this.router.listen(
            Routes.EventTrackerUpcoming,
            () => {
                this.setPage(Routes.EventTrackerUpcoming);
                this.trackEventTrackerPageLoadMixPanel(TRACKING_EVENTS.EVENT_TRACKER_PAGE_LOAD_UPCOMING);
            },
        ));
        this.subscriptions.push(this.router.listen(
            Routes.EventTrackerPast,
            () => {
                this.setPage(Routes.EventTrackerPast);
                this.trackEventTrackerPageLoadMixPanel(TRACKING_EVENTS.EVENT_TRACKER_PAGE_LOAD_PAST);
            },
        ));
        this.subscriptions.push(this.router.listen(
            Routes.EventTrackerDelivered,
            () => {
                this.setPage(Routes.EventTrackerDelivered);
                this.trackEventTrackerPageLoadMixPanel(TRACKING_EVENTS.EVENT_TRACKER_PAGE_LOAD_DELIVERED);
            },
        ));
    };

    private initEventTrackerVM = (route: Routes) => {
        const vm = new EventTrackerVM(
            route,
            this.editEvent,
            this.onPlanningNotesClick,
            this.isPlanningNotesFormShown
        );
        vm.didMount();

        return vm;
    };

    @mobx.action
    private setPage = (page: Routes) => {
        this.page = page;
    };

    @mobx.action
    public editEvent = (event: IEventTrackerDetailsDTO, composedId: string, intl: IntlShape) => {
        const existingSubWindowForThisEvent = this.composedIdToSubWindowMap[composedId];

        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisEvent && existingSubWindowForThisEvent.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisEvent.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            this.composedIdToSubWindowMap[composedId] = this.subWindowService.showSubWindow({
                title: intl.formatMessage({
                    id: `oe.event.editEvent`,
                    description: 'Label text',
                    defaultMessage: 'Edit Event',
                }),
                componentToHost: EventForm,
                componentToHostProps: {
                    accountId: event.accountId,
                    eventId: event.eventId,
                    saveCompletedCallback: () => {
                        this.fetchEventsList();
                    },
                    deleteCompletedCallback: () => {
                        this.completeEventDeletion(`${event.accountId}_${event.eventId}`);
                    },
                },
            });
        }
    };

    private completeEventDeletion = (composeId: string) => {
        this.fetchEventsList();
        this.closeEditForm(composeId);
    };

    private closeEditForm(id: string) {
        this.composedIdToSubWindowMap[id].close();
    }

    private fetchEventsList = () => {
        this.getPageVM().fetchEventsList();
    };

    public getPageVM = (): EventTrackerVM => {
        switch (this.page) {
            case Routes.EventTrackerDelivered:
                return this.deliveredEventTrackerVM;
            case Routes.EventTrackerPast:
                return this.pastEventTrackerVM;
            default:
                return this.upcomingEventTrackerVM;
        }
    };

    @mobx.action
    public onPlanningNotesClick = (accountId: number, eventId: number, composedId: string, intl: IntlShape) => {
        const existingSubWindowForThisEvent = this.composedIdToSubWindowMap[composedId];

        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisEvent && existingSubWindowForThisEvent.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisEvent.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            const planningNotesFormVM = new PlanningNotesFormVM(eventId, accountId, intl, this.fetchEventsList, this.trackEventNotesMixPanel);

            this.composedIdToSubWindowMap[composedId] = this.subWindowService.showSubWindow({
                title: intl.formatMessage({
                    id: 'oe.common.editPlanningNotes',
                    defaultMessage: 'Edit Planning Notes',
                    description: 'Sub-window header message'
                }),
                componentToHost: PlanningNotesForm,
                componentToHostProps: {
                    vm: planningNotesFormVM
                },
                onCloseIconClickHandler: ()=> {
                    this.composedIdToSubWindowMap[composedId].close();
                    this.trackEventNotesMixPanel(false);
                    return true;
                }
            });
        }
    };

    public trackEventNotesMixPanel = (dataSaved: boolean): void => {
        const trackData = {
            'Data saved': convertBoolTypeForMixPanel(dataSaved)
        };
        this.trackingService.get().track(TRACKING_EVENTS.EDIT_PLANNING_NOTES, trackData);
    };

    public isPlanningNotesFormShown = (composedId: string) => {
        return (
            this.composedIdToSubWindowMap[composedId] &&
            this.composedIdToSubWindowMap[composedId].getStatus() !== SubWindowStatus.Closed
        );
    };

    private trackEventTrackerPageLoadMixPanel = (eventName: string): void => {
        this.trackingService.get().track(eventName, {});
    };
}

export { ManageEventTrackerViewModal };
